import { useLayoutEffect, useState } from 'react';

const ROW_TOP_TOLERANCE_PX = 4;
const VIEWPORT_VISIBILITY_THRESHOLD = 0.5;

export type SuggestedFilterPosition = {
  index: number;
  inViewport: boolean;
  row: number;
};

function isChipInViewport(chipRect: DOMRect, containerRect: DOMRect): boolean {
  const visibleLeft = Math.max(chipRect.left, containerRect.left, 0);
  const visibleRight = Math.min(
    chipRect.right,
    containerRect.right,
    window.innerWidth
  );
  const visibleTop = Math.max(chipRect.top, containerRect.top, 0);
  const visibleBottom = Math.min(
    chipRect.bottom,
    containerRect.bottom,
    window.innerHeight
  );

  const visibleArea =
    Math.max(0, visibleRight - visibleLeft) *
    Math.max(0, visibleBottom - visibleTop);
  const chipArea =
    (chipRect.right - chipRect.left) * (chipRect.bottom - chipRect.top);

  return (
    chipArea > 0 && visibleArea / chipArea >= VIEWPORT_VISIBILITY_THRESHOLD
  );
}

export default function useSuggestedFilterChipPositions(
  containerRef: React.RefObject<HTMLElement | null>,
  itemCount: number,
  measurementKey: string
): Array<SuggestedFilterPosition> | undefined {
  const [positions, setPositions] = useState<Array<SuggestedFilterPosition>>();

  useLayoutEffect(() => {
    if (itemCount === 0 || !containerRef.current) return;

    const chipElements = containerRef.current.querySelectorAll<HTMLElement>(
      '[data-suggested-filter-index]'
    );

    if (chipElements.length !== itemCount) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const chips = Array.from(chipElements).map(element => {
      const rect = element.getBoundingClientRect();

      return {
        index: Number(element.dataset.suggestedFilterIndex),
        rect,
        rowBucket: Math.round(rect.top / ROW_TOP_TOLERANCE_PX)
      };
    });

    const rowBuckets = [...new Set(chips.map(chip => chip.rowBucket))].sort(
      (a, b) => a - b
    );

    setPositions(
      chips.map(chip => ({
        index: chip.index,
        inViewport: isChipInViewport(chip.rect, containerRect),
        row: rowBuckets.indexOf(chip.rowBucket) + 1
      }))
    );
  }, [containerRef, itemCount, measurementKey]);

  return positions;
}
